import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Radar, CheckCircle2, RotateCcw, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { usePageMeta } from "@/lib/seo";
import { trackClick } from "@/lib/analytics";
import { PrivacyMention } from "@/components/site/LeadForms";

export default function FlashDiagPage() {
  const [diag, setDiag] = useState(null);
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [lead, setLead] = useState({ prenom: "", nom: "", email: "", entreprise: "", fonction: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  usePageMeta("FlashDiag Agilité", diag?.accroche);

  useEffect(() => {
    api.get("/flashdiag").then((r) => setDiag(r.data)).catch(() => {});
  }, []);

  const answer = async (idx) => {
    const next = [...answers, idx];
    setAnswers(next);
    if (next.length === diag.questions.length) {
      const r = await api.post("/flashdiag/submit", { answers: next });
      setResult(r.data);
    } else {
      setStep(step + 1);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers([]);
    setResult(null);
    setSent(false);
    setLead({ prenom: "", nom: "", email: "", entreprise: "", fonction: "" });
  };

  const sendResult = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/flashdiag/send-result", { ...lead, answers });
      setSent(true);
      trackClick("flashdiag_result_email");
      toast.success("Résultat envoyé ! Vérifiez votre boîte mail.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (!diag)
    return (
      <main className="bg-[#0b0f19] min-h-screen">
        <Nav />
        <div className="pt-40 text-center text-slate-500">Chargement…</div>
      </main>
    );

  const q = diag.questions[step];
  const progress = result ? 100 : Math.round((answers.length / diag.questions.length) * 100);
  const color = result?.result?.color || "#0d9488";

  return (
    <main className="bg-[#0b0f19] min-h-screen" data-testid="flashdiag-page">
      <Nav />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <div className="inline-flex items-center gap-2 border border-amber-400/30 bg-amber-400/5 px-3 py-1.5">
          <Radar size={13} className="text-amber-400" />
          <span className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-amber-300">Mini-diagnostic · {diag.duration}</span>
        </div>
        <h1 className="mt-5 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-50" data-testid="flashdiag-title">
          FlashDiag <span className="grad-text">Agilité</span>
        </h1>

        <div className="mt-8 h-1 bg-white/5">
          <div className="h-full grad-line transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <AnimatePresence mode="wait">
          {step === -1 && !result && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="mt-10">
              <p className="text-slate-300 text-base leading-relaxed">{diag.accroche}</p>
              <button data-testid="flashdiag-start-btn" onClick={() => { trackClick("flashdiag_start"); setStep(0); }} className="btn-primary mt-8">
                Commencer le FlashDiag
              </button>
            </motion.div>
          )}

          {step >= 0 && !result && q && (
            <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }} className="mt-10">
              <div className="font-mono2 text-xs text-slate-500">
                Question {step + 1} / {diag.questions.length}
              </div>
              <h2 className="mt-3 font-heading text-lg sm:text-xl font-semibold text-slate-100" data-testid="flashdiag-question">
                {q.question}
              </h2>
              <div className="mt-6 space-y-3">
                {q.options.map((opt, i) => (
                  <button
                    key={i}
                    data-testid={`flashdiag-option-${i}`}
                    onClick={() => answer(i)}
                    className="w-full text-left border border-white/10 bg-[#131b2c] px-5 py-4 text-sm text-slate-300 hover:border-teal-400/50 hover:bg-[#16233a] transition-colors"
                  >
                    <span className="font-mono2 text-teal-400 mr-3">{String.fromCharCode(65 + i)}</span>
                    {opt}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {result && (
            <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10" data-testid="flashdiag-result">
              <div className="border p-8 text-center" style={{ borderColor: `${color}66`, background: "#0f1524" }}>
                <div className="font-heading text-5xl font-extrabold" style={{ color }} data-testid="flashdiag-score">
                  {result.score}<span className="text-2xl text-slate-500"> / {result.total}</span>
                </div>
                {result.result && (
                  <>
                    <div className="mt-4 inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
                      <h2 className="font-heading text-xl font-semibold text-slate-100">{result.result.title}</h2>
                    </div>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{result.result.message}</p>
                  </>
                )}
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <button onClick={restart} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm border border-white/15 text-slate-300 hover:border-teal-400/50 transition-colors" data-testid="flashdiag-restart-btn">
                    <RotateCcw size={14} /> Recommencer
                  </button>
                  <Link to="/#contact" className="btn-primary !py-2.5" data-testid="flashdiag-contact-cta">
                    Découvrir le Diagnostic Agile
                  </Link>
                </div>
              </div>

              {!sent ? (
                <form onSubmit={sendResult} className="mt-6 border border-white/10 bg-[#111827] p-6" data-testid="flashdiag-email-form">
                  <div className="flex items-center gap-2 text-slate-100">
                    <Mail size={16} className="text-teal-300" />
                    <h3 className="font-heading text-base font-semibold">Recevoir le résultat de mon FlashDiag</h3>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Laissez vos coordonnées pour recevoir votre score détaillé et nos recommandations.</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <input data-testid="flashdiag-email-prenom" className="input-dark" placeholder="Prénom" value={lead.prenom} onChange={(e) => setLead({ ...lead, prenom: e.target.value })} required />
                    <input data-testid="flashdiag-email-nom" className="input-dark" placeholder="Nom" value={lead.nom} onChange={(e) => setLead({ ...lead, nom: e.target.value })} required />
                    <input data-testid="flashdiag-email-email" type="email" className="input-dark" placeholder="E-mail" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required />
                    <input data-testid="flashdiag-email-entreprise" className="input-dark" placeholder="Entreprise *" value={lead.entreprise} onChange={(e) => setLead({ ...lead, entreprise: e.target.value })} required />
                    <input data-testid="flashdiag-email-fonction" className="input-dark" placeholder="Fonction *" value={lead.fonction} onChange={(e) => setLead({ ...lead, fonction: e.target.value })} required />
                  </div>
                  <button data-testid="flashdiag-email-submit" disabled={sending} className="btn-primary mt-4 disabled:opacity-60">
                    {sending ? "Envoi…" : "Recevoir mon résultat"}
                  </button>
                  <PrivacyMention />
                </form>
              ) : (
                <div className="mt-6 border border-teal-400/30 bg-[#10201f] p-6 text-center" data-testid="flashdiag-email-sent">
                  <CheckCircle2 size={20} className="text-teal-400 mx-auto" />
                  <p className="mt-2 text-sm text-slate-200">Votre résultat détaillé vous a été envoyé par email.</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <Footer />
    </main>
  );
}
